import React from 'react';

import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';

import {
  addLayerToMap,
  getAllClickedFeatures,
  getAllFeaturesByDetail,
  removeComponentLayers,
  zoomToExtentWithBuffer,
} from 'utils/map/map';
import { addFeaturesToLayer, createLayer } from 'utils/map/layers';
import {
  hasArrayElements,
  hasArrayJustOneElement,
  hasObjectKeys,
} from 'utils/main';
import { setDroneFilterTypes } from 'store/actions/map/dronesFilters';
import socketContext from 'components/contentWrapper/socketContext';
import mqttContext from 'components/contentWrapper/mqttContext';
import UserSessionDataHandler from 'auth/UserSessionsDataHandler';
import { setDashboardSelectedUavos } from 'store/actions/dashboard/selectedUavos';
import DisplayUavos from './displayUavos/DisplayDetails';

import {
  centerMapToCurrentUavoPosition,
  deduplicateAircrafts,
  getAllUavosInsideExtent,
  parseDrones,
  setBasicUavoIconStyle,
  setSelectedUavoStyle,
  updateSelectedVehicleData,
} from './utils/common';

import { filterUavosSources, updateUavosOnMap } from './utils/updateUavosLayer';
import { trackUavo } from './utils/trackUavo';

const DRONE_GRACE_PERIOD_MS = 7000;

const mergeDronesWithGracePeriod = (previousDrones, freshDrones, lastSeenMap) => {
  const now = Date.now();
  const freshIds = new Set();

  for (const drone of freshDrones) {
    const key = drone.id || drone.uid;
    freshIds.add(key);
    lastSeenMap.set(key, now);
  }

  const merged = [...freshDrones];

  for (const prevDrone of previousDrones) {
    const key = prevDrone.id || prevDrone.uid;
    if (!freshIds.has(key)) {
      const lastSeen = lastSeenMap.get(key);
      if (lastSeen && now - lastSeen < DRONE_GRACE_PERIOD_MS) {
        merged.push(prevDrone);
      } else {
        lastSeenMap.delete(key);
      }
    }
  }

  return merged;
};

const OperationDetails = props => {
  const { selectedUavos, map, mappingUavos } = props;

  const dispatch = useDispatch();
  const socket = React.useContext(socketContext);
  const mqttClient = React.useContext(mqttContext);
  const uavosLayer = React.useRef();
  const trackerLayer = React.useRef();
  const receiverPopup = React.useRef(null);
  const receiverPopupOverlay = React.useRef();
  const selectedUavosRef = React.useRef([]);
  const mappingUavosRef = React.useRef(null);
  const followSelectedUavoRef = React.useRef(false);
  const mapRef = React.useRef(null);
  const aircrafts = React.useRef([]);
  const drones = React.useRef([]);
  const utmDrones = React.useRef([]);
  const droneLastSeen = React.useRef(new Map());

  React.useEffect(() => {
    trackerLayer.current = createLayer('trackerLayer');
    uavosLayer.current = createLayer('uavosLayer');
  }, []);

  React.useEffect(() => {
    selectedUavosRef.current = selectedUavos;
  }, [selectedUavos, map]);

  const initMap = () => {
    if (!hasObjectKeys(map)) return;
    map.on('click', handleClick);
    addLayerToMap(map, trackerLayer.current);
    addLayerToMap(map, uavosLayer.current);
    addSelectedUavoToLayer();
    mapRef.current = map;
    return () => cleanup();
  };

  React.useEffect(initMap, [map]);

  const cleanup = () => {
    removeComponentLayers(map);
    map.un('click', handleClick);
    droneLastSeen.current.clear();
  };

  const handleClick = event => {
    if (!event) return;
    const uavosFeatures = getAllFeaturesByDetail(
      getAllClickedFeatures(map, event),
      'uavo'
    );
    if (hasArrayJustOneElement(selectedUavosRef.current))
      setBasicUavoIconStyle(selectedUavosRef.current[0], mappingUavos);
    dispatch(setDashboardSelectedUavos(uavosFeatures));
  };

  const addSelectedUavoToLayer = () => {
    addFeaturesToLayer(uavosLayer.current, selectedUavosRef.current);
  };

  const handleUavos = allUavos => {
    if (!hasArrayElements(allUavos) || !hasObjectKeys(mappingUavosRef.current))
      return;
    updateUavosOnMap(
      uavosLayer.current,
      getFilteredUavos(allUavos),
      mappingUavosRef.current
    );

    trackUavo(selectedUavosRef.current, trackerLayer.current);
    updateSelectedUavoWhenOneIsSelected(allUavos);
    handleCenterMapToSelectedUavoPosition();
  };

  const getFilteredUavos = allUavos =>
    filterUavosSources(allUavos, UserSessionDataHandler.getTmpFilters());

  const updateSelectedUavoWhenOneIsSelected = allUavos => {
    if (hasArrayJustOneElement(selectedUavosRef.current)) {
      updateSelectedVehicleData(selectedUavosRef.current[0], allUavos);
    }
  };

  const handleCenterMapToSelectedUavoPosition = () => {
    if (followSelectedUavoRef.current) {
      centerMapToCurrentUavoPosition(mapRef.current, selectedUavosRef.current);
    }
  };

  React.useEffect(() => {
    mappingUavosRef.current = mappingUavos;
  }, [mappingUavos]);

  const displayMergedUavos = () => {
    const dedupedAircrafts = deduplicateAircrafts(
      drones.current,
      aircrafts.current
    );
    handleUavos(
      getAllUavosInsideExtent(
        [...drones.current, ...dedupedAircrafts, ...utmDrones.current],
        UserSessionDataHandler.getDashboardExtent()
      )
    );
    getAndSetZonesFilters();
  };

  React.useEffect(() => {
    if (!socket) return;
    socket.on('uavosPositions', uavosData => {
      if (uavosData === 'no data') return;
      utmDrones.current = uavosData;
      displayMergedUavos();
    });

    return () => socket.off('uavosPositions');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  React.useEffect(() => {
    if (!socket) return;
    socket.on('dronesPositions', socketDrones => {
      if (socketDrones === 'no data') return;
      const freshDrones = parseDrones(socketDrones);
      drones.current = mergeDronesWithGracePeriod(
        drones.current,
        freshDrones,
        droneLastSeen.current
      );
      displayMergedUavos();
    });

    return () => socket.off('dronesPositions');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  React.useEffect(() => {
    if (!mqttClient) return;
    mqttClient.on('message', (topic, encodedMessage, packet) => {
      aircrafts.current = JSON.parse(encodedMessage);
      displayMergedUavos();
    });
  }, [mqttClient]);

  const getAndSetZonesFilters = () => {
    const uavoSources = new Set(
      drones.current.map(droneObject => droneObject.source)
    );
    const allSources = [...uavoSources, 'UTM'];
    dispatch(setDroneFilterTypes(allSources));

    const currentTmpFilters = UserSessionDataHandler.getTmpFilters();
    if (currentTmpFilters.length === 0) {
      const savedFilters = UserSessionDataHandler.getUavosFilters();
      if (savedFilters.length > 0) {
        UserSessionDataHandler.saveTmpFilters(savedFilters);
      } else {
        UserSessionDataHandler.saveTmpFilters(allSources);
        UserSessionDataHandler.saveUavosFilters(allSources);
      }
    }
  };

  React.useEffect(() => {
    if (!hasObjectKeys(receiverPopupOverlay.current)) return;
    receiverPopupOverlay.current.setElement(receiverPopup.current);
  }, [receiverPopup]);

  const handleOneUavoSelected = () => {
    if (!hasObjectKeys(map)) return;
    if (hasArrayJustOneElement(selectedUavosRef.current)) {
      setSelectedUavoStyle(selectedUavosRef.current[0], mappingUavos);
    } else followSelectedUavoRef.current = false;
    trackUavo(selectedUavosRef.current, trackerLayer.current);
  };

  React.useEffect(handleOneUavoSelected, [selectedUavos, map]);

  const followVehiclePopup = () => {
    followSelectedUavoRef.current = !followSelectedUavoRef.current;
  };

  const zoomToUavo = id => {
    for (const eachUavo of selectedUavosRef.current) {
      if (eachUavo.getProperties().data.id === id) {
        const uavoExtent = eachUavo.getGeometry().getExtent();
        zoomToExtentWithBuffer(map, uavoExtent);
        return;
      }
    }
  };

  return <DisplayUavos follow={followVehiclePopup} zoomToUavo={zoomToUavo} />;
};

OperationDetails.propTypes = {
  selectedUavos: PropTypes.array.isRequired,
  map: PropTypes.object.isRequired,
  mappingUavos: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
  map: state.map,
  mappingUavos: state.mappingUavos,
  selectedUavos: state.selectedDashboardUavos,
});

export default connect(mapStateToProps)(React.memo(OperationDetails));
